import {Link, NavLink} from "react-router-dom";
import {DEFAULT_GUEST, PATHS} from "../../../constants";
import logo from "../../../img/logos/fotm-logo-motion.svg";
import {useAtom} from "jotai/index";
import {
    AcademicCapIcon,
    ArrowDownTrayIcon,
    ArrowLeftStartOnRectangleIcon,
    ArrowUpTrayIcon,
    CircleStackIcon,
    FolderIcon,
    HomeModernIcon,
    MegaphoneIcon
} from "@heroicons/react/24/outline";
import {Collection} from "../../../models/Collection";
import {loadCollectionsHook, userAtom} from "./Storage";
import {Button} from "@headlessui/react";
import {logout} from "../../../api";

const navigation = [
    {
        name: 'Database',
        href: PATHS.SEARCH,
        icon: CircleStackIcon,
        current: true,
        requiresAuth: false,
        requiresAdmin: false,
        newTab: false
    },
    {
        name: 'Collections',
        href: PATHS.COLLECTIONS,
        icon: FolderIcon,
        current: false,
        requiresAuth: true,
        requiresAdmin: false,
        newTab: false
    },
    {
        name: 'Downloads',
        href: PATHS.DOWNLOADS,
        icon: ArrowDownTrayIcon,
        current: false,
        requiresAuth: false,
        requiresAdmin: false,
        newTab: false
    },
    {
        name: 'Importer Tool',
        href: PATHS.IMPORTS,
        icon: ArrowUpTrayIcon,
        current: false,
        requiresAuth: true,
        requiresAdmin: true,
        newTab: false
    },
    {
        name: 'Project Homepage',
        href: 'https://freedomonthemove.org',
        icon: HomeModernIcon,
        current: false,
        requiresAuth: false,
        requiresAdmin: false,
        newTab: true
    },
    {
        name: 'Educator\'s Portal',
        href: 'https://freedomonthemove.org/educators',
        icon: AcademicCapIcon,
        current: false,
        requiresAuth: false,
        requiresAdmin: false,
        newTab: true
    },
    {
        name: 'Feedback Form',
        href: 'https://cornell.ca1.qualtrics.com/jfe/form/SV_8p5UREqM0bjwWns',
        icon: MegaphoneIcon,
        current: false,
        requiresAuth: false,
        requiresAdmin: false,
        newTab: true
    }
]

function showNavItem(user: any, n: any): boolean {
    if (n.requiresAuth && !user?.id) {
        return false;
    }

    if (n.requiresAdmin && !user?.isAdmin) {
        return false;
    }

    return true;
}

export default function Sidebar() {

    const [user, setUser] = useAtom(userAtom);

    const {
        data: collections,
        isSuccess: collectionsLoaded,
        isLoadingError: collectionsLoadingError
    } = loadCollectionsHook(user);

    return (
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col border-r border-indigo-600">
            <div
                className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white text-white px-6">
                <div className="flex h-16 shrink-0 items-center">
                    <Link to={PATHS.SEARCH}>
                        <img
                            className="block h-24 mt-24 py-2 md:py-0 w-auto"
                            src={logo}
                            alt="Freedom on the Move"
                        />
                    </Link>
                </div>
                <nav className="flex flex-1 flex-col mt-20">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                        <li>
                            <ul role="list" className="-mx-2 space-y-1">
                                {navigation.filter(n => showNavItem(user, n)).map((item) => (
                                    <li key={item.name}>
                                        <NavLink to={item.href} target={item.newTab ? "_blank" : "_self"}
                                                 className={({isActive}) =>
                                                     (isActive ? "bg-gray-50 text-indigo-600" : "text-gray-700 hover:text-indigo-600 hover:bg-gray-50")
                                                     + " group flex gap-x-3 p-2 text-sm leading-6 font-semibold"}>
                                            {({isActive}) => (<>
                                                <item.icon
                                                    className={(isActive ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600') + " h-6 w-6 shrink-0"}
                                                    aria-hidden="true"
                                                />
                                                {item.name}
                                            </>)}
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </li>
                        {collectionsLoaded && !collectionsLoadingError && collections.length > 0 &&
                            <li>
                                <div className="text-md font-semibold leading-6 text-gray-700">Your Collections
                                </div>
                                <ul role="list" className="-mx-2 mt-2 space-y-1">
                                    {collections.slice(0, 5).map((collection: Collection) =>
                                        <li key={collection.id}>
                                            <Link to={PATHS.SINGLE_COLLECTION.replace(":id", collection.id)}
                                                  className='text-gray-700 hover:text-indigo-600 hover:bg-gray-50 group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'>
                                                <span className="truncate">{collection.name}</span>
                                            </Link>
                                        </li>
                                    )}
                                    {collections.length >= 5 &&
                                        <>
                                            <hr/>
                                            <li>
                                                <Link to={PATHS.COLLECTIONS}
                                                      className='text-gray-700 hover:text-indigo-600 hover:bg-gray-50 group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'>
                                                    View All Collections
                                                </Link>
                                            </li>
                                        </>
                                    }
                                </ul>
                            </li>
                        }
                        <li className="-mx-6 mt-auto">
                            {user?.id ?
                                <div
                                    className="flex items-center gap-x-4 mb-2 px-6 py-3 text-md font-semibold leading-6 text-gray-900 hover:bg-gray-50">
                                    <Button onClick={async () => {
                                        await logout();
                                        setUser(DEFAULT_GUEST);
                                    }}>
                                        Logout <ArrowLeftStartOnRectangleIcon
                                        className="inline ml-2 h-6 w-6"/>
                                    </Button>
                                </div> :
                                <Link
                                    to={PATHS.LOGIN}
                                    className="flex items-center gap-x-4 mb-2 px-6 py-3 text-lg font-semibold leading-6 text-gray-900 hover:bg-gray-50">
                                    <span className="sr-only">Login / Register {user.id}</span>
                                    <span aria-hidden="true">Login / Register</span>
                                </Link>
                            }
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    )
        ;
}